/* ═══════════════════════ DOKUMENTVAULT ═══════════════════════
   Supabase Auth + Private Storage. Pass, forsikring, bookinger.
════════════════════════════════════════════════════════════════ */

import { S } from './state.js';
import { getSB, SB_READY } from './cloud.js';
import { esc, irow, compressImage, dataUrlToBlob } from './utils.js';
import { HOTEL_INFO } from './data.js';
import { renderPage, renderNav } from './main.js';
import {
  vaultList, vaultAdd, vaultGetBlob, vaultDelete,
  hasPin, setPin, verifyPin, isUnlocked, lock as vaultLockSession, removePin,
} from './vault.js';

const DOC_BUCKET="travel-docs";
const DOC_CATEGORIES=["Pass","Forsikring","Booking","eTA","Medisinsk","Annet"];
export const DOC_STATE={session:null,docs:[],loading:false,authMode:"login",authMsg:"",uploading:0,uploadTotal:0,uploadMsg:""};

/* ── AI-indeksering av dokumenter ─────────────────────────────────
   Hvert dokument får et tekstekstrakt ({docId}.ai.txt) lagret ved siden av
   originalen i samme private bucket — så AI-chatten (get_documents-verktøyet)
   kan lese innholdet uten å laste ned/tolke PDF-en på nytt hver gang.
   Ingen skjemaendring i Supabase: RLS-policyen på uid-mappen dekker filen. */
const AI_INDEX_STATE={busy:false,done:0,total:0,msg:"",indexed:null,stale:null}; // indexed/stale: Set<docId> | null
const _aiTextCache=new Map(); // docId → {version,text} | null (null = ekstrakt finnes ikke)

/* Ekstrakt-versjon. Sidecar-filene får en versjonslinje øverst, slik at et
   ekstrakt laget av en ELDRE ekstraktor kjennes igjen og re-indekseres
   automatisk ved neste oppslag. Uten dette lever gamle, avkortede ekstrakter
   videre for alltid (tidsstempel-sjekken ser bare på om ORIGINALEN er endret)
   — det var grunnen til at detaljer på side 5, f.eks. AVIS-henteadressen,
   fortsatt manglet lenge etter at token-taket i doc-extract ble hevet.
   v2 = doc-extract med max_tokens 8000 (hele fler-siders ordrebekreftelser). */
const AI_EXTRACT_VERSION=2;
const stampExtract=t=>`#ai-extract v${AI_EXTRACT_VERSION}\n${t}`;
function parseExtract(raw){
  const m=/^#ai-extract v(\d+)\r?\n/.exec(raw||"");
  return m?{version:+m[1],text:raw.slice(m[0].length)}:{version:1,text:raw||""};
}

/** Henter (og cacher) ett ekstrakt. null = ekstrakt finnes ikke.
    Nettverksfeil caches IKKE — ellers ville en midlertidig feil utløst en
    unødvendig og kostbar re-ekstraksjon. */
async function loadExtract(docId){
  if(_aiTextCache.has(docId))return _aiTextCache.get(docId);
  const client=getSB();const uid=DOC_STATE.session?.user?.id;
  if(!client||!uid)return null;
  try{
    const {data}=await client.storage.from(DOC_BUCKET).download(`${uid}/${docId}.ai.txt`);
    const entry=data?parseExtract(await data.text()):null;
    _aiTextCache.set(docId,entry);
    return entry;
  }catch{return null;}
}

/** Markerer ekstrakter laget av en ELDRE ekstraktor som «må leses på nytt», og
    starter re-indekseringen. Kjøres i bakgrunnen ved innlogging slik at
    oppgraderingen normalt er ferdig før brukeren spør AI-en om noe. */
async function aiVersionSweep(){
  if(!getSB()||!DOC_STATE.session)return;
  if(AI_INDEX_STATE.indexed===null)await aiIndexRefresh();
  let outdated=0;
  for(const d of DOC_STATE.docs){
    if(!aiIndexable(d)||!AI_INDEX_STATE.indexed?.has(d.id))continue;
    const entry=await loadExtract(d.id);
    if(entry&&entry.version<AI_EXTRACT_VERSION){AI_INDEX_STATE.stale?.add(d.id);outdated++;}
  }
  if(outdated){
    if(S.tab==="klar"&&S.sub==="dok")renderPage();
    await aiIndexAll(true);
  }
}

// Personvern: passdokumenter deles ALDRI med AI — de indekseres ikke i det
// hele tatt. (Ekstraktoren skjermer i tillegg pass-/personnummer i andre
// dokumenter, og systemprompten forbyr AI å referere slike opplysninger.)
const AI_EXCLUDED_CATEGORIES=new Set(["Pass"]);
function aiIndexable(d){return !AI_EXCLUDED_CATEGORIES.has(d.category);}

function aiTextPath(docId){
  const uid=DOC_STATE.session?.user?.id;
  return uid?`${uid}/${docId}.ai.txt`:null;
}

/** Status for AI-ekstraktene: hvilke finnes, og hvilke er utdaterte?
    «Utdatert» = originalfilen har nyere tidsstempel enn ekstraktet
    (f.eks. erstattet fil) — den re-indekseres av aiIndexAll. */
async function aiIndexRefresh(){
  const client=getSB();const uid=DOC_STATE.session?.user?.id;
  if(!client||!uid){AI_INDEX_STATE.indexed=new Set();AI_INDEX_STATE.stale=new Set();return;}
  try{
    const {data}=await client.storage.from(DOC_BUCKET).list(uid,{limit:1000});
    const sidecars=new Map(),originals=new Map(); // docId → tidsstempel
    for(const f of data||[]){
      const ts=Date.parse(f.updated_at||f.created_at||"")||0;
      if(f.name.endsWith(".ai.txt"))sidecars.set(f.name.replace(/\.ai\.txt$/,""),ts);
      else originals.set(f.name.replace(/\.[^.]+$/,""),ts);
    }
    AI_INDEX_STATE.indexed=new Set(sidecars.keys());
    AI_INDEX_STATE.stale=new Set();
    for(const [id,ts] of sidecars){
      const orig=originals.get(id);
      if(orig&&orig>ts+2000){ // 2 s slingring — sidecar skrives normalt like etter originalen
        AI_INDEX_STATE.stale.add(id);
        _aiTextCache.delete(id); // tving ny nedlasting etter re-indeksering
      }
    }
  }catch{AI_INDEX_STATE.indexed=new Set();AI_INDEX_STATE.stale=new Set();}
  if(S.tab==="klar"&&S.sub==="dok")renderPage();
}

/** Indekserer ett dokument: signert URL → Claude-ekstrakt → lagre .ai.txt.
    Claude henter filen selv fra den signerte URL-en, så størrelsen begrenses
    ikke av Vercels body-grense (store PDF-er feilet med base64-varianten).
    Returnerer {ok, err} slik at feil kan vises per dokument. */
async function aiIndexDoc(doc){
  const client=getSB();const path=aiTextPath(doc.id);
  if(!client||!path)return {ok:false,err:"ikke innlogget"};
  try{
    const {data:signed,error:sErr}=await client.storage.from(DOC_BUCKET).createSignedUrl(doc.file_path,300);
    if(sErr||!signed?.signedUrl)return {ok:false,err:"fikk ikke signert URL"};
    const r=await fetch("/api/doc-extract",{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({url:signed.signedUrl,mime:doc.mime,label:doc.label}),
    });
    if(!r.ok){
      let detail="";try{detail=(await r.json()).error||"";}catch{}
      return {ok:false,err:`HTTP ${r.status}${detail?" — "+detail:""}`};
    }
    const {text}=await r.json();
    if(!text)return {ok:false,err:"tomt ekstrakt"};
    const up=await client.storage.from(DOC_BUCKET).upload(path,new Blob([stampExtract(text)],{type:"text/plain"}),{contentType:"text/plain",upsert:true});
    if(up.error)return {ok:false,err:"lagring av ekstrakt feilet"};
    _aiTextCache.set(doc.id,{version:AI_EXTRACT_VERSION,text});
    AI_INDEX_STATE.indexed?.add(doc.id);
    return {ok:true};
  }catch(e){console.warn("AI-indeksering feilet",doc.label,e);return {ok:false,err:String(e?.message||e)};}
}

/** Indekserer alle dokumenter som mangler ekstrakt eller er endret siden sist.
    Kjøres automatisk ved innlogging, etter opplasting, og fra get_documents-
    verktøyet — samt manuelt via knappen. `silent` = ingen kvitteringsmelding. */
export async function aiIndexAll(silent=false,force=false){
  if(AI_INDEX_STATE.busy)return;
  const client=getSB();
  if(!client||!DOC_STATE.session)return;
  if(AI_INDEX_STATE.indexed===null)await aiIndexRefresh();
  // force=true re-ekstraherer ALLE dokumenter, også de som allerede er indeksert
  // — nødvendig når ekstraktoren er forbedret (f.eks. høyere token-tak) slik at
  // gamle, avkortede sidecar-ekstrakter genereres på nytt.
  const missing=DOC_STATE.docs.filter(d=>aiIndexable(d)&&(force||!AI_INDEX_STATE.indexed.has(d.id)||AI_INDEX_STATE.stale?.has(d.id)));
  if(!missing.length){
    if(!silent){AI_INDEX_STATE.msg="✅ Alle dokumenter er allerede gjort kjent for AI-assistenten (og ingen er endret siden sist).";renderPage();}
    return;
  }
  if(force)missing.forEach(d=>_aiTextCache.delete(d.id)); // tving fersk nedlasting
  AI_INDEX_STATE.busy=true;AI_INDEX_STATE.done=0;AI_INDEX_STATE.total=missing.length;AI_INDEX_STATE.msg="";
  if(!silent)renderPage();
  let ok=0;const fails=[];
  for(const d of missing){
    const res=await aiIndexDoc(d);
    if(res.ok){ok++;AI_INDEX_STATE.stale?.delete(d.id);}
    else fails.push({label:d.label,err:res.err});
    AI_INDEX_STATE.done++;
    if(S.tab==="klar"&&S.sub==="dok")renderPage();
  }
  AI_INDEX_STATE.busy=false;AI_INDEX_STATE.total=0;
  if(!silent||fails.length){
    AI_INDEX_STATE.msg=!fails.length
      ?`✅ ${ok} dokument${ok===1?"":"er"} lest og gjort kjent for AI-assistenten.`
      :`⚠️ ${ok} av ${missing.length} indeksert. Feilet: ${fails.slice(0,3).map(f=>`«${f.label}» (${f.err})`).join(", ")}${fails.length>3?` +${fails.length-3} til`:""}. Prøv igjen — vedvarer det, si fra hva som står her.`;
  }
  if(S.tab==="klar"&&S.sub==="dok")renderPage();
}

/* ── Søk i ekstraktene ────────────────────────────────────────────────
   Med 9+ dokumenter (ordrebekreftelser, Guest Ticket, flybilletter …) sprenger
   samlet ekstrakt-tekst tegnbudsjettet for ett chat-kall. Før klippet vi bare
   toppen av hvert dokument og BRØT løkken når budsjettet var brukt opp — uten å
   fortelle AI-en om det. Da «fantes» ikke resten av ordrebekreftelsen, og
   assistenten svarte i god tro at henteadressen for leiebilen ikke sto noe
   sted. Nå kan AI-en søke seg fram til de relevante avsnittene i stedet. */
const norm=s=>String(s||"").toLowerCase();
const QUERY_STOPWORDS=new Set(["hvor","hvordan","hvilken","hvilke","skal","jeg","vi","hva","når","hvem","er","og","på","til","for","den","det","de","en","et","av","med","om","min","mitt","mine","vår","våre","kan","du","som","fra","har","ikke","der","dette","disse","står","finn","finner","hente","henter"]);

function queryTerms(q){
  return [...new Set(norm(q).split(/[^a-z0-9æøåäöüé]+/).filter(w=>w.length>=3&&!QUERY_STOPWORDS.has(w)))];
}
function countHits(text,terms){
  const t=norm(text);
  return terms.reduce((sum,w)=>sum+(t.split(w).length-1),0);
}
/** Utdrag rundt treffene (±4 linjer, slått sammen) — beholder de relevante
    avsnittene uansett hvor i dokumentet de står, i stedet for å klippe toppen. */
function excerptAround(text,terms,max){
  const lines=text.split("\n");
  const keep=new Set();
  lines.forEach((l,i)=>{
    if(!terms.some(w=>norm(l).includes(w)))return;
    for(let j=Math.max(0,i-4);j<=Math.min(lines.length-1,i+4);j++)keep.add(j);
  });
  if(!keep.size)return text.slice(0,max);
  const out=[];let prev=-1,len=0;
  for(const i of [...keep].sort((a,b)=>a-b)){
    if(prev>=0&&i>prev+1)out.push("…");
    out.push(lines[i]);
    len+=lines[i].length+1;prev=i;
    if(len>=max){out.push("… [utdrag avkortet — be om hele dokumentet med labels]");break;}
  }
  return out.join("\n");
}

/**
 * Innholdet i brukerens AI-indekserte dokumenter — brukes av get_documents-
 * verktøyet i AI-chatten.
 * @param {{query?:string,labels?:string[]}} [input]
 *   query  — fritekst; returnerer utdrag rundt treffene i dokumentene som matcher
 *   labels — hent bestemte dokumenter i sin helhet (delvis navnematch holder)
 * Uten argumenter returneres alt som får plass innenfor tegnbudsjettet.
 * Returnerer alltid `index` (ALLE indekserte dokumenter) og `omitted`, slik at
 * assistenten vet hva som finnes og aldri konkluderer med at noe «ikke står i
 * dokumentene» når det bare er kuttet av budsjettgrunner.
 */
export async function getDocsForAI(input={}){
  const client=getSB();
  if(!client||!DOC_STATE.session){
    return {error:"Brukeren er ikke innlogget i dokumentarkivet. Be dem logge inn under Klar → Dokumenter, så får du tilgang til dokumentinnholdet."};
  }
  if(!DOC_STATE.docs.length)await docsLoad();
  if(!DOC_STATE.docs.length)return {documents:[],note:"Ingen dokumenter er lastet opp ennå."};
  // Ferskhetssjekk: fanger nye opplastinger (også fra andre enheter) og
  // endrede filer. Selve re-indekseringen startes i bakgrunnen lenger ned.
  await aiIndexRefresh();

  const query=typeof input?.query==="string"?input.query.trim():"";
  const wantLabels=Array.isArray(input?.labels)
    ? input.labels.map(s=>norm(s).trim()).filter(Boolean)
    : [];

  // 1) Last inn alle ekstrakter (cachet) og oppdag utdaterte ekstrakt-versjoner.
  const loaded=[];let unindexed=0;
  for(const d of DOC_STATE.docs){
    if(!aiIndexable(d))continue; // passdokumenter deles aldri med AI
    const entry=await loadExtract(d.id);
    if(!entry?.text){unindexed++;continue;}
    // Eldre ekstraktor → merk som utdatert så bakgrunnsjobben lager det på nytt.
    if(entry.version<AI_EXTRACT_VERSION)AI_INDEX_STATE.stale?.add(d.id);
    loaded.push({doc:d,text:entry.text});
  }

  const index=loaded.map(({doc,text})=>({
    label:doc.label,category:doc.category,
    uploaded:(doc.created_at||"").slice(0,10),chars:text.length,
  }));

  // 2) Velg ut hva som skal med.
  let selected=loaded,mode="alle";
  if(wantLabels.length){
    const hit=loaded.filter(({doc})=>{
      const l=norm(doc.label);
      return wantLabels.some(w=>l.includes(w)||w.includes(l));
    });
    if(hit.length){selected=hit;mode="labels";}
  }else if(query){
    const terms=queryTerms(query);
    const scored=loaded.map(e=>({...e,hits:countHits(e.text,terms)}))
      .filter(e=>e.hits>0).sort((a,b)=>b.hits-a.hits);
    if(scored.length){selected=scored;mode="søk";}
  }

  // 3) Budsjettér. Per-dok-taket MÅ være ≥ maks ekstraktstørrelse (doc-extract
  //    lager opptil ~8000 tokens ≈ 32k tegn), ellers kappes et fullstendig
  //    ekstrakt ned igjen. 90k totalt holder god margin til MAX_BODY_CHARS=200k
  //    når systemprompt, historikk og JSON-escaping legges oppå.
  const PER_DOC_CHARS=45_000,TOTAL_CHARS=90_000;
  const terms=mode==="søk"?queryTerms(query):[];
  const documents=[],omitted=[],included=new Set();
  let budget=TOTAL_CHARS;
  for(const {doc,text} of selected){
    if(budget<=0)continue; // tas med i `omitted` under
    const cap=Math.min(PER_DOC_CHARS,budget);
    let content=text,clipped=false;
    if(text.length>cap){
      content=terms.length?excerptAround(text,terms,cap):text.slice(0,cap);
      clipped=true;
    }
    budget-=content.length;
    included.add(doc.id);
    documents.push({
      label:doc.label,category:doc.category,
      uploaded:(doc.created_at||"").slice(0,10),
      ...(clipped?{partial:terms.length?"utdrag rundt søketreffene":"kun starten av dokumentet"}:{}),
      content,
    });
  }
  for(const {doc} of loaded)if(!included.has(doc.id))omitted.push(doc.label);

  const out={mode,documents,index};
  const notes=[];
  if(omitted.length){
    out.omitted=omitted;
    notes.push(`Disse dokumentene er IKKE med i svaret over: ${omitted.map(l=>`«${l}»`).join(", ")}. De finnes i arkivet — kall get_documents på nytt med labels:["…"] eller query:"…" hvis svaret kan stå der. Ikke konkluder med at noe mangler i dokumentene før du har sjekket dem.`);
  }
  if(documents.some(d=>d.partial)){
    notes.push(`Noen dokumenter er forkortet (se «partial»). Trenger du hele teksten, kall get_documents med labels:["<dokumentnavn>"].`);
  }
  const staleCount=DOC_STATE.docs.filter(d=>aiIndexable(d)&&AI_INDEX_STATE.stale?.has(d.id)).length;
  if(unindexed||staleCount){
    aiIndexAll(true); // start (re)indeksering i bakgrunnen — ikke vent på Claude-kall her
    if(unindexed)notes.push(`${unindexed} dokument(er) var ikke AI-indeksert ennå — indeksering er startet i bakgrunnen; spør igjen om ca. et halvt minutt for komplett svar.`);
    if(staleCount)notes.push(`${staleCount} dokument(er) er endret eller lest med en eldre tekstuttrekker — innholdet over kan mangle detaljer fra de siste sidene. Re-indeksering pågår nå i bakgrunnen; si fra til brukeren at de bør stille spørsmålet på nytt om et halvt minutt.`);
  }
  const excluded=DOC_STATE.docs.filter(d=>!aiIndexable(d)).length;
  if(excluded)notes.push(`${excluded} passdokument(er) holdes utenfor AI av personvernhensyn — pass-/personnummer skal aldri refereres.`);
  if(notes.length)out.note=notes.join(" ");
  return out;
}

/* ── #3 Offline-vault (IndexedDB, PIN-låst, fungerer uten nett) ── */
const VAULT_STATE={list:null,loading:false,uploading:0,uploadTotal:0,pinMsg:"",mode:"view"};

async function vaultRefresh(){
  if(VAULT_STATE.loading) return;
  VAULT_STATE.loading=true;
  try{ VAULT_STATE.list=await vaultList(); }catch{ VAULT_STATE.list=[]; }
  VAULT_STATE.loading=false;
  if(S.tab==="klar"&&S.sub==="dok") renderPage();
}

export async function vaultUpload(inputEl){
  const cat=document.getElementById("vault-cat")?.value||"Annet";
  const files=Array.from(inputEl.files||[]);
  if(!files.length) return;
  VAULT_STATE.uploading=0;VAULT_STATE.uploadTotal=files.length;renderPage();
  for(const file of files){
    try{
      let blob=file;let mime=file.type||"application/octet-stream";
      if(mime.startsWith("image/")&&mime!=="image/gif"){
        const c=await compressImage(file,1600,0.85);
        blob=dataUrlToBlob(c.dataUrl);mime=c.mime; // ikke fetch(data:) — blokkeres av CSP connect-src
      }
      await vaultAdd({label:file.name.replace(/\.[^.]+$/,""),category:cat,mime,blob});
    }catch(e){console.warn("Vault add error",e);}
    VAULT_STATE.uploading++;renderPage();
  }
  VAULT_STATE.uploading=0;VAULT_STATE.uploadTotal=0;
  inputEl.value="";
  await vaultRefresh();
}

export async function vaultOpen(id){
  const blob=await vaultGetBlob(id);
  if(!blob){alert("Fant ikke filen lokalt.");return;}
  const url=URL.createObjectURL(blob);
  if(blob.type.startsWith("image/")){
    const lb=document.createElement("div");
    lb.className="lb-overlay";
    lb.style.cssText="position:fixed;inset:0;background:rgba(0,0,0,.92);z-index:9999;display:flex;align-items:center;justify-content:center";
    lb.innerHTML=`<img src="${url}" style="max-width:96vw;max-height:92vh;object-fit:contain;border-radius:8px">
      <button style="position:absolute;top:12px;right:12px;background:rgba(0,0,0,.7);border:none;border-radius:50%;width:40px;height:40px;color:#fff;cursor:pointer;font-size:20px" aria-label="Lukk">✕</button>`;
    const close=()=>{lb.remove();URL.revokeObjectURL(url);};
    lb.querySelector("button").onclick=close;
    lb.addEventListener("click",e=>{if(e.target===lb)close();});
    document.body.appendChild(lb);
  }else{
    window.open(url,"_blank","noopener");
    setTimeout(()=>URL.revokeObjectURL(url),60000);
  }
}

export async function vaultRemove(id){
  if(!confirm("Slette offline-kopien fra denne enheten?"))return;
  await vaultDelete(id);
  await vaultRefresh();
}

export async function vaultUnlock(){
  const pin=document.getElementById("vault-pin")?.value||"";
  if(await verifyPin(pin)){VAULT_STATE.pinMsg="";await vaultRefresh();}
  else{VAULT_STATE.pinMsg="Feil PIN.";renderPage();}
}

export async function vaultSavePin(){
  const pin=document.getElementById("vault-newpin")?.value||"";
  if(pin.length<4){VAULT_STATE.pinMsg="PIN må være minst 4 siffer.";renderPage();return;}
  await setPin(pin);
  VAULT_STATE.mode="view";VAULT_STATE.pinMsg="";renderPage();
}

export function vaultStartPin(){VAULT_STATE.mode="setpin";VAULT_STATE.pinMsg="";renderPage();}
export function vaultLock(){vaultLockSession();renderPage();}
export function vaultRemovePin(){
  if(!confirm("Fjerne PIN-låsen? Offline-dokumentene blir tilgjengelige uten kode."))return;
  removePin();renderPage();
}

function renderVault(){
  // Last listen første gang (asynkront) — vis «laster …» imens.
  if(VAULT_STATE.list===null && !VAULT_STATE.loading) vaultRefresh();
  const locked=!isUnlocked();
  const head=`<div class="sec-lbl" style="margin-top:18px">📴 Offline-koffert <span style="font-weight:400;color:var(--t3)">· på enheten, virker uten nett</span></div>`;

  if(VAULT_STATE.mode==="setpin"){
    return `${head}<div class="card">
      <div style="font-size:12.5px;color:var(--t2);line-height:1.6;margin-bottom:10px">Sett en PIN (min. 4 siffer) som låser offline-dokumentene mot innsyn hvis noen plukker opp telefonen.</div>
      <div class="fg"><input id="vault-newpin" class="fi" type="password" inputmode="numeric" autocomplete="off" placeholder="Ny PIN"></div>
      ${VAULT_STATE.pinMsg?`<div style="font-size:12px;color:var(--red);margin-bottom:8px">${esc(VAULT_STATE.pinMsg)}</div>`:""}
      <div style="display:flex;gap:8px"><button class="btn btn-gold btn-sm" style="flex:1" onclick="vaultSavePin()">Lagre PIN</button>
        <button class="btn btn-ghost btn-sm" onclick="vaultCancelPin()">Avbryt</button></div>
    </div>`;
  }

  if(locked){
    return `${head}<div class="card">
      <div style="text-align:center;font-size:30px;margin-bottom:6px">🔒</div>
      <div style="font-size:13px;color:var(--t2);text-align:center;margin-bottom:12px">Offline-kofferten er låst</div>
      <div class="fg"><input id="vault-pin" class="fi" type="password" inputmode="numeric" autocomplete="off" placeholder="PIN" onkeydown="if(event.key==='Enter')vaultUnlock()"></div>
      ${VAULT_STATE.pinMsg?`<div style="font-size:12px;color:var(--red);margin-bottom:8px">${esc(VAULT_STATE.pinMsg)}</div>`:""}
      <button class="btn btn-gold btn-sm" style="width:100%" onclick="vaultUnlock()">Lås opp</button>
    </div>`;
  }

  const list=VAULT_STATE.list||[];
  const upPct=VAULT_STATE.uploadTotal?Math.round(100*VAULT_STATE.uploading/VAULT_STATE.uploadTotal):0;
  const totalMb=(list.reduce((s,d)=>s+(d.size||0),0)/1048576).toFixed(1);

  return `${head}<div class="card">
    <div style="font-size:12px;color:var(--t2);line-height:1.6;margin-bottom:10px">📸 Ta bilde av pass, forsikringsbevis, eTA og billetter — lagres <strong>kun på denne enheten</strong> og åpner uten nett ved grensa.</div>
    <div class="fg"><label class="fl">Kategori</label>
      <select id="vault-cat" class="fi">${DOC_CATEGORIES.map(c=>`<option value="${c}">${c}</option>`).join("")}</select></div>
    <div style="display:flex;gap:8px;flex-wrap:wrap">
      <label for="vault-file" class="btn btn-gold btn-sm" style="cursor:pointer">📥 Legg til</label>
      <input type="file" id="vault-file" accept="image/*,application/pdf" capture="environment" multiple style="display:none" onchange="vaultUpload(this)">
      ${hasPin()
        ?`<button class="btn btn-ghost btn-sm" onclick="vaultLock()">🔒 Lås</button><button class="btn btn-ghost btn-sm" onclick="vaultRemovePin()">Fjern PIN</button>`
        :`<button class="btn btn-ghost btn-sm" onclick="vaultStartPin()">🔑 Sett PIN</button>`}
    </div>
    ${VAULT_STATE.uploadTotal?`<div style="font-size:12px;color:var(--t2);margin-top:8px">Lagrer ${VAULT_STATE.uploading}/${VAULT_STATE.uploadTotal} (${upPct}%)…</div>`:""}
    ${list.length?`<div style="font-size:10.5px;color:var(--t3);margin-top:10px">${list.length} dokument(er) · ${totalMb} MB lokalt</div>`:""}
  </div>
  ${list.length?`<div class="card" style="margin-top:10px">
    ${list.map(d=>{
      const isImg=d.mime&&d.mime.startsWith("image/");
      const kb=d.size?Math.round(d.size/1024):0;
      return `<div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border)">
        <div style="font-size:22px">${isImg?"🖼️":"📄"}</div>
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:600;color:var(--t1);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(d.label||"(uten navn)")}</div>
          <div style="font-size:11px;color:var(--t3)">${esc(d.category||"")} · ${kb} KB</div>
        </div>
        <button class="btn btn-ghost btn-sm" onclick="vaultOpen('${d.id}')">Åpne</button>
        <button class="btn btn-red btn-sm" onclick="vaultRemove('${d.id}')" aria-label="Slett offline-kopi">🗑</button>
      </div>`;
    }).join("")}
  </div>`:`<div style="text-align:center;color:var(--t3);font-size:12.5px;margin:12px 0">Ingen offline-dokumenter ennå.</div>`}`;
}

// Liten hjelper for «Avbryt» i sett-PIN-modus (eksponert via main.js).
export function vaultCancelPin(){VAULT_STATE.mode="view";VAULT_STATE.pinMsg="";renderPage();}

export async function docsInit(){
  const client=getSB();if(!client)return;
  const {data}=await client.auth.getSession();
  DOC_STATE.session=data?.session||null;
  // Auto-synk AI-ekstrakter i bakgrunnen: først manglende/endrede dokumenter,
  // deretter versjons-sveipet som fornyer ekstrakter fra en eldre ekstraktor.
  if(DOC_STATE.session){await docsLoad();aiIndexAll(true).then(aiVersionSweep);}
  client.auth.onAuthStateChange((_e,session)=>{
    DOC_STATE.session=session||null;
    try{renderNav();}catch{}   // oppdater global kontostatus-indikator (sidemeny + bunnmeny)
    if(session){docsLoad().then(()=>{if(S.tab==="klar"&&S.sub==="dok")renderPage();aiIndexAll(true).then(aiVersionSweep);});}
    else{DOC_STATE.docs=[];AI_INDEX_STATE.indexed=null;AI_INDEX_STATE.stale=null;_aiTextCache.clear();if(S.tab==="klar"&&S.sub==="dok")renderPage();}
  });
}

async function docsLoad(){
  const client=getSB();if(!client||!DOC_STATE.session)return;
  DOC_STATE.loading=true;
  try{
    const {data,error}=await client.from("travel_documents").select("*").order("created_at",{ascending:false});
    if(!error&&data)DOC_STATE.docs=data;
  }catch(e){console.warn("Docs load error",e);}
  DOC_STATE.loading=false;
}

// Oversetter Supabase-feilmeldinger til tydelig norsk handling.
function authErr(msg){
  const m=(msg||"").toLowerCase();
  if(m.includes("email not confirmed"))
    return "❌ Kontoen er ikke bekreftet. Slå av «Confirm email» i Supabase (Authentication → Providers → Email) og bekreft/slett+opprett brukeren på nytt.";
  if(m.includes("invalid login credentials"))
    return "❌ Feil e-post/passord – eller kontoen finnes ikke ennå. Trykk «Opprett» hvis du ikke har konto.";
  if(m.includes("already registered")||m.includes("already been registered"))
    return "❌ E-posten er allerede registrert. Trykk «Logg inn» i stedet.";
  return "❌ "+msg;
}

export async function docsSignIn(){
  const email=(document.getElementById("doc-email")?.value||"").trim();
  const pw=document.getElementById("doc-pw")?.value||"";
  if(!email||!pw){DOC_STATE.authMsg="Fyll inn e-post og passord.";renderPage();return;}
  const client=getSB();
  if(!client){DOC_STATE.authMsg="⚠️ Innloggingstjenesten kunne ikke lastes – blokkeres den av en annonseblokker/tracking-vern? Last siden helt på nytt.";renderPage();return;}
  DOC_STATE.authMsg="Logger inn…";renderPage();
  const {error}=await client.auth.signInWithPassword({email,password:pw});
  if(error){DOC_STATE.authMsg=authErr(error.message);renderPage();}
  else{DOC_STATE.authMsg="";}
}

export async function docsSignUp(){
  const email=(document.getElementById("doc-email")?.value||"").trim();
  const pw=document.getElementById("doc-pw")?.value||"";
  if(!email||pw.length<8){DOC_STATE.authMsg="Passord må være minst 8 tegn.";renderPage();return;}
  const client=getSB();
  if(!client){DOC_STATE.authMsg="⚠️ Innloggingstjenesten kunne ikke lastes – blokkeres den av en annonseblokker/tracking-vern? Last siden helt på nytt.";renderPage();return;}
  DOC_STATE.authMsg="Oppretter konto…";renderPage();
  const {data,error}=await client.auth.signUp({email,password:pw});
  if(error){DOC_STATE.authMsg=authErr(error.message);renderPage();}
  else if(data?.session){DOC_STATE.authMsg="";}   // auto-bekreftet → onAuthStateChange logger deg inn
  else{DOC_STATE.authMsg="✅ Konto opprettet. Trykk «Logg inn» hvis du ikke blir logget inn automatisk.";renderPage();}
}

export async function docsSignOut(){
  const client=getSB();if(!client)return;
  await client.auth.signOut();
}

export function docsSetAuthMode(m){DOC_STATE.authMode=m;DOC_STATE.authMsg="";renderPage();}

// Oversetter en opplastingsfeil til en konkret handling (typisk manglende oppsett).
function uploadFailHint(err){
  const e=(err||"").toLowerCase();
  if(e.includes("bucket not found"))
    return "Storage-arkivet mangler: opprett en PRIVAT bucket «travel-docs» i Supabase (Storage → New bucket).";
  if(e.includes("could not find the table")||e.includes("schema cache")||e.includes("travel_documents"))
    return "Databasen mangler tabellen «travel_documents» – kjør oppsett-SQL-en (supabase-docs-setup.sql) i Supabase.";
  if(e.includes("row-level security")||e.includes("violates row-level")||e.includes("policy"))
    return "Mangler tilgangsregler (RLS-policy) – kjør oppsett-SQL-en i Supabase.";
  if(e.includes("payload too large")||e.includes("exceeded")||e.includes("too large"))
    return "Filen er for stor for arkivet.";
  return "Detalj: "+err;
}

export async function docsUpload(inputEl,forceCat){
  const client=getSB();
  if(!client){DOC_STATE.uploadMsg="⚠️ Innloggingstjenesten er ikke lastet. Last siden på nytt.";renderPage();return;}
  if(!DOC_STATE.session){DOC_STATE.uploadMsg="⚠️ Du må være innlogget for å laste opp.";renderPage();return;}
  const category=forceCat||document.getElementById("doc-cat")?.value||"Annet";
  const files=Array.from(inputEl.files||[]);
  if(!files.length)return;
  DOC_STATE.uploading=0;DOC_STATE.uploadTotal=files.length;DOC_STATE.uploadMsg="";renderPage();
  const uid=DOC_STATE.session.user.id;
  let ok=0; const fails=[];
  for(const file of files){
    try{
      let blob=file;let mime=file.type||"application/octet-stream";let ext=(file.name.split(".").pop()||"bin").toLowerCase();
      if(mime.startsWith("image/")&&mime!=="image/gif"){
        const c=await compressImage(file,1600,0.85);
        blob=dataUrlToBlob(c.dataUrl); // ikke fetch(data:) — blokkeres av CSP connect-src
        mime=c.mime;ext=c.ext;
      }
      const id=crypto.randomUUID();
      const path=`${uid}/${id}.${ext}`;
      const {error:upErr}=await client.storage.from(DOC_BUCKET).upload(path,blob,{contentType:mime,upsert:false});
      if(upErr){console.warn("Storage upload error",upErr);fails.push({name:file.name,err:upErr.message||String(upErr)});DOC_STATE.uploading++;renderPage();continue;}
      const label=file.name.replace(/\.[^.]+$/,"");
      const {error:dbErr}=await client.from("travel_documents").insert({
        id,user_id:uid,category,label,file_path:path,mime,size:blob.size
      });
      if(dbErr){console.warn("DB insert error",dbErr);await client.storage.from(DOC_BUCKET).remove([path]);fails.push({name:file.name,err:dbErr.message||String(dbErr)});DOC_STATE.uploading++;renderPage();continue;}
      ok++;
    }catch(e){console.warn("Upload error",e);fails.push({name:file.name,err:String(e?.message||e)});}
    DOC_STATE.uploading++;renderPage();
  }
  DOC_STATE.uploading=0;DOC_STATE.uploadTotal=0;
  inputEl.value="";
  // Tydelig kvittering: hvor mange gikk bra, hva feilet og hvorfor.
  if(!fails.length){
    DOC_STATE.uploadMsg=`✅ ${ok} fil${ok===1?"":"er"} lastet opp og lagret i ditt private arkiv.`;
  }else{
    const okPart=ok?`✅ ${ok} lastet opp. `:"";
    DOC_STATE.uploadMsg=`${okPart}❌ ${fails.length} feilet. ${uploadFailHint(fails[0].err)}`;
  }
  await docsLoad();renderPage();
  // Gjør nye dokumenter kjent for AI-assistenten i bakgrunnen (én gang per fil)
  if(ok)aiIndexAll();
}

export async function docsView(id){
  const client=getSB();if(!client)return;
  const doc=DOC_STATE.docs.find(d=>d.id===id);if(!doc)return;
  const {data,error}=await client.storage.from(DOC_BUCKET).createSignedUrl(doc.file_path,300);
  if(error||!data){alert("Kunne ikke åpne fil: "+(error?.message||"ukjent"));return;}
  if(doc.mime&&doc.mime.startsWith("image/")){
    const lb=document.createElement("div");
    lb.className="lb-overlay";
    lb.style.cssText="position:fixed;inset:0;background:rgba(0,0,0,.92);z-index:9999;display:flex;align-items:center;justify-content:center";
    lb.innerHTML=`<img src="${data.signedUrl}" style="max-width:96vw;max-height:92vh;object-fit:contain;border-radius:8px">
      <button onclick="this.parentElement.remove()" style="position:absolute;top:12px;right:12px;background:rgba(0,0,0,.7);border:none;border-radius:50%;width:40px;height:40px;color:#fff;cursor:pointer;font-size:20px" aria-label="Lukk">✕</button>`;
    lb.addEventListener("click",e=>{if(e.target===lb)lb.remove();});
    document.body.appendChild(lb);
  }else{
    window.open(data.signedUrl,"_blank","noopener");
  }
}

export async function docsDelete(id){
  if(!confirm("Slette dokumentet?"))return;
  const client=getSB();if(!client)return;
  const doc=DOC_STATE.docs.find(d=>d.id===id);if(!doc)return;
  const aiPath=aiTextPath(id);
  await client.storage.from(DOC_BUCKET).remove(aiPath?[doc.file_path,aiPath]:[doc.file_path]);
  await client.from("travel_documents").delete().eq("id",id);
  DOC_STATE.docs=DOC_STATE.docs.filter(d=>d.id!==id);
  AI_INDEX_STATE.indexed?.delete(id);_aiTextCache.delete(id);
  renderPage();
}

export function renderDok(){
  const bookings=[
    ["🚢 Cruise","6158158 · AquaClass 1563, Dekk 11"],
    ["✈️ Icelandair","Z2IIVW"],
    ["✈️ WestJet","KMXQYA"],
    ["🚗 AVIS (Flexible Autos)","4H985592"],
    ["📋 USA Tours","FZJQ4P · NO13752"],
    ...HOTEL_INFO.map(h=>[`🏨 ${h.n}`,h.ref])
  ];
  // Nicholas reiser på samme tur, men med egen bestilling (USA Tours NO13827).
  const nicholasBookings=[
    ["🚢 Cruise","7202457 · Veranda 6108, Dekk 6"],
    ["✈️ Icelandair","YUDTUF"],
    ["✈️ WestJet","CBNLIG"],
    ["🏨 DoubleTree Kamloops","9020852289804 · egen romreservasjon · 26.–27. jul"],
    ["🏨 Jasper Inn & Suites","HTL-WBD-997415335 · 27.–29. jul · eget hotell (Cavell Penthouse Suite)"],
    ["📋 USA Tours","H7FZLR · NO13827"],
  ];
  // De FULLSTENDIGE reisedokumentene (PDF) hører hjemme i det private,
  // innloggede arkivet — ikke i den offentlig publiserte appen.
  // Sjekklista under matcher opplastede filer på filnavn og viser hva som mangler.
  const REISEDOK=[
    // ── Robert & Margita (USA Tours NO13752) ──
    {navn:"Robert & Margita – Ordrebekreftelse",sub:"USA Tours NO13752 · full reise",ok:d=>/ordre/i.test(d.label||"")&&!/nicholas/i.test(d.label||"")},
    {navn:"Robert & Margita – Flybilletter",sub:"Icelandair Z2IIVW · WestJet KMXQYA",ok:d=>/(flyreise|flybillett)/i.test(d.label||"")&&!/nicholas/i.test(d.label||"")},
    {navn:"Cruise-dokumenter (Guest Ticket)",sub:"Celebrity Solstice · booking 6158158",ok:d=>/cruise/i.test(d.label||"")},
    {navn:"Bagasjelapper – Robert & Margita",sub:"AquaClass 1563",ok:d=>/(luggage|bagasje)/i.test(d.label||"")&&!/nicholas/i.test(d.label||"")},
    // ── Nicholas (USA Tours NO13827) ──
    {navn:"Nicholas – Ordrebekreftelse",sub:"USA Tours NO13827 · egen bestilling",ok:d=>/ordre/i.test(d.label||"")&&/nicholas/i.test(d.label||"")},
    {navn:"Nicholas – Flybilletter",sub:"egen retur via Toronto · YUDTUF / CBNLIG",ok:d=>/(flyreise|flybillett)/i.test(d.label||"")&&/nicholas/i.test(d.label||"")},
    {navn:"Bagasjelapper – Nicholas",sub:"Veranda 6108",ok:d=>/(luggage|bagasje)/i.test(d.label||"")&&/nicholas/i.test(d.label||"")},
    // ── Felles ──
    {navn:"Guide til nødtelefon",sub:"USA Tours akutthjelp",ok:d=>/(n(ø|o)dtelefon|emergency)/i.test(d.label||"")},
    {navn:"Huskeliste før avreise",sub:"sjekkliste",ok:d=>/huskeliste/i.test(d.label||"")},
  ];
  const loggedIn=!!DOC_STATE.session;
  const reisedokRows=REISEDOK.map(r=>{
    const hit=loggedIn?DOC_STATE.docs.find(r.ok):null;
    const status=!loggedIn
      ?`<span style="font-size:11px;color:var(--t3);white-space:nowrap">🔒 krever innlogging</span>`
      :hit
        ?`<button class="btn btn-ghost btn-sm" onclick="docsView('${hit.id}')">Åpne ✓</button>`
        :`<span style="font-size:11px;color:var(--gold2);white-space:nowrap">○ ikke lastet opp</span>`;
    return `<div style="display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid var(--border)">
      <div style="font-size:20px">📄</div>
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;font-weight:600;color:var(--t1)">${esc(r.navn)}</div>
        <div style="font-size:11px;color:var(--t3)">${esc(r.sub)}</div>
      </div>${status}
    </div>`;
  }).join("");
  // Felles opplastings-tilbakemelding (fremdrift ELLER kvittering) – vises der man laster opp.
  const uploadFeedback = DOC_STATE.uploadTotal
    ? `<div style="margin-top:9px;font-size:12px;color:var(--t2)">⏳ Laster opp ${DOC_STATE.uploading}/${DOC_STATE.uploadTotal} (${Math.round(100*DOC_STATE.uploading/Math.max(1,DOC_STATE.uploadTotal))}%)…</div>`
    : (DOC_STATE.uploadMsg
        ? `<div style="margin-top:9px;padding:9px 12px;border-radius:8px;font-size:12.5px;line-height:1.5;color:var(--t1);${DOC_STATE.uploadMsg.startsWith("✅")?"background:rgba(52,211,153,.12);border:1px solid rgba(52,211,153,.32)":"background:rgba(248,113,113,.1);border:1px solid rgba(248,113,113,.3)"}">${esc(DOC_STATE.uploadMsg)}</div>`
        : "");
  const reisedokCard=`<div class="sec-lbl">📄 Reisedokumenter <span style="font-weight:400;color:var(--t3)">· privat arkiv</span></div>
    <div class="card" style="margin-bottom:14px">
      ${reisedokRows}
      <div style="font-size:11.5px;color:var(--t3);line-height:1.55;margin-top:10px">
        De fullstendige PDF-bekreftelsene lagres <strong>kun i ditt innloggede arkiv</strong> (RLS – ingen andre kan lese dem), ikke i den offentlige appen.
        ${loggedIn
          ?`<div style="margin-top:9px"><label for="reisedok-file" class="btn btn-gold btn-sm" style="cursor:pointer">📥 Last opp bekreftelser (flere om gangen)</label>
             <input type="file" id="reisedok-file" accept="application/pdf" multiple style="display:none" onchange="docsUpload(this,'Booking')">
             <div style="font-size:10.5px;color:var(--t3);margin-top:5px">Tips: hold inne Ctrl/⌘ eller Shift for å velge flere PDF-er samtidig.</div></div>`
          :`<div style="margin-top:7px;color:var(--gold2)">Logg inn nedenfor for å laste opp eller åpne dem.</div>`}
      </div>
      ${loggedIn?uploadFeedback:""}
    </div>`;

  const bookingsHtml=`<div class="sec-lbl">📚 Bekreftelser &amp; referanser</div>
    <div class="card" style="margin-bottom:14px">${bookings.map(b=>irow(b[0],b[1])).join("")}</div>
    <div class="sec-lbl">👤 Nicholas – egen bestilling</div>
    <div class="card" style="margin-bottom:14px">${nicholasBookings.map(b=>irow(b[0],b[1])).join("")}</div>
    ${reisedokCard}`
    +renderVault();

  if(!SB_READY){
    return `<div class="page-title">🔐 Dokumenter</div>
      <div class="page-sub">Supabase er ikke konfigurert</div>${bookingsHtml}`;
  }

  if(!DOC_STATE.session){
    const isLogin=DOC_STATE.authMode!=="signup";
    return `<div class="page-title">🔐 Dokumenter</div>
      <div class="page-sub">Logg inn for å se passbilder og kopier — kryptert lagring, kun du har tilgang</div>
      ${bookingsHtml}
      <div class="card" style="max-width:420px;margin:0 auto">
        <div class="sec-lbl" style="margin-top:0">${isLogin?"Logg inn":"Opprett konto"}</div>
        <div class="fg"><label class="fl">E-post</label>
          <input id="doc-email" class="fi" type="email" autocomplete="email" placeholder="din@epost.no"></div>
        <div class="fg"><label class="fl">Passord${isLogin?"":" (min. 8 tegn)"}</label>
          <input id="doc-pw" class="fi" type="password" autocomplete="${isLogin?"current-password":"new-password"}"></div>
        ${DOC_STATE.authMsg?`<div style="font-size:12px;color:var(--t2);margin:8px 0">${esc(DOC_STATE.authMsg)}</div>`:""}
        <div style="display:flex;gap:8px;margin-top:10px">
          ${isLogin
            ?`<button class="btn btn-gold btn-sm" onclick="docsSignIn()" style="flex:1">Logg inn</button>
              <button class="btn btn-ghost btn-sm" onclick="docsSetAuthMode('signup')">Ny konto</button>`
            :`<button class="btn btn-gold btn-sm" onclick="docsSignUp()" style="flex:1">Opprett</button>
              <button class="btn btn-ghost btn-sm" onclick="docsSetAuthMode('login')">Logg inn</button>`}
        </div>
        <div style="font-size:11px;color:var(--t3);margin-top:12px;line-height:1.55">
          🔒 Filer lagres privat i Supabase. RLS sikrer at kun din bruker kan lese dem. Kryptert i transit (HTTPS) og i ro (AES-256).
        </div>
      </div>`;
  }

  const byCat={};
  DOC_CATEGORIES.forEach(c=>byCat[c]=[]);
  DOC_STATE.docs.forEach(d=>{(byCat[d.category]||byCat["Annet"]).push(d);});
  const email=DOC_STATE.session.user?.email||"";

  // AI-indekserings-kort: status + knapp. Teller lastes asynkront første gang.
  if(AI_INDEX_STATE.indexed===null&&!AI_INDEX_STATE.busy)aiIndexRefresh();
  const aiDocs=DOC_STATE.docs.filter(aiIndexable);
  const idxCount=AI_INDEX_STATE.indexed?aiDocs.filter(d=>AI_INDEX_STATE.indexed.has(d.id)).length:null;
  const staleCount=AI_INDEX_STATE.stale?aiDocs.filter(d=>AI_INDEX_STATE.stale.has(d.id)).length:0;
  const allIdx=idxCount!==null&&idxCount===aiDocs.length&&aiDocs.length>0&&staleCount===0;
  const aiCard=`<div class="card" style="margin-bottom:14px">
    <div class="sec-lbl" style="margin-top:0">🧠 AI-assistenten &amp; dokumentene</div>
    <div style="font-size:12.5px;color:var(--t2);line-height:1.6;margin-bottom:10px">
      Reiseassistenten kan svare på alt som står i dokumentene — referansenumre, tider, seter, lugar,
      vilkår og frister. Nye og endrede filer fanges opp automatisk (ved innlogging, etter opplasting og
      når du spør AI-en); knappen er for å tvinge en synk med en gang. Ekstraktet lagres i ditt private arkiv (RLS).
      <strong>Personvern:</strong> passdokumenter deles aldri med AI, og pass-/personnummer skjermes fra alle ekstrakter.
    </div>
    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
      <button class="btn btn-gold btn-sm" onclick="aiIndexAll()" ${AI_INDEX_STATE.busy?"disabled":""}>
        ${AI_INDEX_STATE.busy?`⏳ Leser ${AI_INDEX_STATE.done}/${AI_INDEX_STATE.total}…`:"🧠 Gjør dokumentene kjent for AI"}
      </button>
      <button class="btn btn-ghost btn-sm" onclick="if(confirm('Les ALLE dokumenter på nytt fra bunnen? Bruk dette hvis AI-en overser detaljer som står i et dokument (f.eks. på en senere side).'))aiIndexAll(false,true)" ${AI_INDEX_STATE.busy?"disabled":""} title="Re-ekstraherer alt på nytt — nyttig hvis et ekstrakt mangler detaljer fra de siste sidene">↻ Full re-indeksering</button>
      <span style="font-size:12px;color:${allIdx?"var(--green)":"var(--t3)"}">
        ${idxCount===null?"Sjekker status…":allIdx?`✓ Alle ${idxCount} dokumenter er kjent for AI`:`${idxCount} av ${aiDocs.length} kjent for AI`}${staleCount?` · ${staleCount} må leses på nytt`:""}${DOC_STATE.docs.length>aiDocs.length?` · ${DOC_STATE.docs.length-aiDocs.length} pass holdes utenfor`:""}
      </span>
    </div>
    ${AI_INDEX_STATE.msg?`<div style="margin-top:9px;padding:9px 12px;border-radius:8px;font-size:12.5px;line-height:1.5;color:var(--t1);${AI_INDEX_STATE.msg.startsWith("✅")?"background:rgba(52,211,153,.12);border:1px solid rgba(52,211,153,.32)":"background:rgba(248,113,113,.1);border:1px solid rgba(248,113,113,.3)"}">${esc(AI_INDEX_STATE.msg)}</div>`:""}
  </div>`;

  return `<div class="page-title">🔐 Dokumenter</div>
    <div style="display:flex;align-items:center;gap:11px;padding:12px 15px;margin-bottom:14px;background:rgba(52,211,153,.1);border:1px solid rgba(52,211,153,.32);border-radius:var(--r2)">
      <span style="width:11px;height:11px;border-radius:50%;background:var(--green);box-shadow:0 0 0 3px rgba(52,211,153,.22);flex-shrink:0"></span>
      <div style="flex:1;min-width:0">
        <div style="font-size:11px;font-weight:800;letter-spacing:.5px;color:var(--green);text-transform:uppercase">✅ Logget inn</div>
        <div style="font-size:13.5px;font-weight:600;color:var(--t1);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(email)}</div>
        <div style="font-size:11px;color:var(--t3);margin-top:1px">${DOC_STATE.docs.length} dokument(er) i ditt private arkiv</div>
      </div>
      <button class="btn btn-ghost btn-sm" onclick="docsSignOut()" style="flex-shrink:0">🚪 Logg ut</button>
    </div>
    ${bookingsHtml}
    ${aiCard}
    <div class="card" style="margin-bottom:14px">
      <div class="sec-lbl" style="margin-top:0">📤 Last opp dokumenter</div>
      <div class="fg"><label class="fl">Kategori</label>
        <select id="doc-cat" class="fi">
          ${DOC_CATEGORIES.map(c=>`<option value="${c}">${c}</option>`).join("")}
        </select></div>
      <div class="fg"><label class="fl">Filer (velg flere om gangen — bilder komprimeres automatisk)</label>
        <input type="file" class="fi" multiple accept="image/*,application/pdf" onchange="docsUpload(this)"></div>
      ${uploadFeedback}
    </div>
    ${DOC_CATEGORIES.map(cat=>{
      const list=byCat[cat];if(!list.length)return"";
      return `<div class="sec-lbl" style="margin-top:14px">${cat} (${list.length})</div>
        <div class="card">
          ${list.map(d=>{
            const isImg=d.mime&&d.mime.startsWith("image/");
            const sizeKb=d.size?Math.round(d.size/1024):0;
            return `<div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border)">
              <div style="font-size:22px">${isImg?"🖼️":"📄"}</div>
              <div style="flex:1;min-width:0">
                <div style="font-size:13px;font-weight:600;color:var(--t1);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(d.label||"(uten navn)")}</div>
                <div style="font-size:11px;color:var(--t3)">${sizeKb} KB · ${new Date(d.created_at).toLocaleDateString("nb-NO")}</div>
              </div>
              <button class="btn btn-ghost btn-sm" onclick="docsView('${d.id}')">Åpne</button>
              <button class="btn btn-red btn-sm" onclick="docsDelete('${d.id}')" aria-label="Slett dokument">🗑</button>
            </div>`;
          }).join("")}
        </div>`;
    }).join("")}
    ${DOC_STATE.docs.length===0?`<div style="text-align:center;color:var(--t3);font-size:13px;margin:20px 0">Ingen dokumenter lastet opp ennå.</div>`:""}
    <div style="margin-top:20px;text-align:center">
      <button onclick="docsSignOut()" class="btn btn-ghost btn-sm">🚪 Logg ut</button>
    </div>
    <div style="font-size:11px;color:var(--t3);margin-top:12px;line-height:1.55;text-align:center">
      🔒 Private signed URLs (5 min). RLS hindrer alle andre i å lese filene.
    </div>`;
}
